import * as React from 'react';

import { translate } from '~l10n/client';

import { AddQuizModalEventAdapter } from '~analytics/adapters/AddQuizModalEventAdapter';
import { SmallButton } from '~shared/components/buttons';
import {
  AddQuizDialog,
  useAddQuizDialog,
} from '~teacher/components/AddQuizDialog';
import { useCreateQuiz } from '~teacher/components/quizzes/QuizListMenu/useCreateQuiz';

interface Props {
  addQuiz: ReturnType<typeof useCreateQuiz>;
  openImportQuizDialog: () => void;
  openAddFolderDialog: () => void;
}

function usePersonalAddQuizDialog() {
  const { isAddQuizDialogOpen, closeAddQuizDialog, openAddQuizDialog } =
    useAddQuizDialog();

  return {
    isAddQuizDialogOpen,
    closeAddQuizDialog,
    openAddQuizDialog: () => {
      new AddQuizModalEventAdapter({
        source: 'personal_library',
        action: 'open',
      }).sendAnalytics();

      openAddQuizDialog();
    },
  };
}

export function QuizPanelControls({
  addQuiz,
  openImportQuizDialog,
  openAddFolderDialog,
}: Props) {
  const { openAddQuizDialog, closeAddQuizDialog, isAddQuizDialogOpen } =
    usePersonalAddQuizDialog();
  const handleGenerateQuestions = async () => {
    new AddQuizModalEventAdapter({
      source: 'personal_library',
      action: 'close',
      close_action: 'ai',
    }).sendAnalytics();

    await addQuiz({
      aiFeature: 'generate',
      onPaywallOpen: () => {
        closeAddQuizDialog();
      },
    });
    closeAddQuizDialog();
  };

  const handleImportFromText = async () => {
    new AddQuizModalEventAdapter({
      source: 'personal_library',
      action: 'close',
      close_action: 'ai-import',
    }).sendAnalytics();

    await addQuiz({
      aiFeature: 'import',
      onPaywallOpen: () => {
        closeAddQuizDialog();
      },
    });
    closeAddQuizDialog();
  };

  const handleBlank = async () => {
    new AddQuizModalEventAdapter({
      source: 'personal_library',
      action: 'close',
      close_action: 'blank',
    }).sendAnalytics();

    await addQuiz({
      onPaywallOpen: () => {
        closeAddQuizDialog();
      },
    });
    closeAddQuizDialog();
  };

  const handleImportQuiz = () => {
    new AddQuizModalEventAdapter({
      source: 'personal_library',
      action: 'close',
      close_action: 'ai',
    }).sendAnalytics();

    closeAddQuizDialog();
    openImportQuizDialog();
  };

  const handleClose = () => {
    new AddQuizModalEventAdapter({
      source: 'personal_library',
      action: 'close',
      close_action: 'close_button',
    }).sendAnalytics();
    closeAddQuizDialog();
  };

  return (
    <>
      <AddQuizDialog
        importQuiz={handleImportQuiz}
        generateQuestions={handleGenerateQuestions}
        importFromText={handleImportFromText}
        createBlank={handleBlank}
        isOpen={isAddQuizDialogOpen}
        onClose={handleClose}
      />

      <SmallButton appearance="default" onClick={openAddFolderDialog}>
        {translate('New Folder')}
      </SmallButton>
      <SmallButton
        onClick={openAddQuizDialog}
        appearance="primary"
        data-testid={'add-library-quiz-button'}
      >
        {translate('Add Quiz')}
      </SmallButton>
    </>
  );
}
